<template>
  <v-card>
    <v-card-title>
      <h2 class="card-title">{{ 'activities.edit_country' | translate }}</h2>
    </v-card-title>
    <v-card-content>
      <div  class="form-element">
        <v-input-r :focus="true" :label="'activities.name' | translate" :hint="errorMessage('name')" :invalid="!!errorMessage('name')"
          v-model="form.name" />
      </div>
      <div  class="form-element">
        <v-select-r :label="$t('history.timezone')" :hint="errorMessage('timezone_name')"
          :invalid="!!errorMessage('timezone_name')" v-model="form.timezone_name" :options="options.timezones"
          itemValue="value" />
      </div>
      <div>
        <v-file-input :hint="errorMessage('logo')" :invalid="!!errorMessage('logo')" :placeholder="'activities.choose' | translate"
          v-model="form.logo" accept="image/*" />
      </div>
      <div v-if="currentLogo && !form.logo" >
        <label class="form-label">{{ 'activities.photo' | translate }}:</label>
        <div class="current-logo">
          <img :src="currentLogo" alt="photo" class="logo-preview" />
        </div>
      </div>
    </v-card-content>
    <v-card-actions>
      <div class="card-actions-wrapper">
        <div class="main-actions">
          <v-btn :disabled="!isValid" :loading="loading" @click="onUpdate">
            {{ $t('base.save') }}
          </v-btn>
        </div>
        <div class="secondary-actions">
          <delete-btn @delete="onDelete" />
        </div>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from '@/api'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'

export default {
  name: 'EditCountryModal',
  components: {
    DeleteBtn
  },
  props: {
    country: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    form: {
      name: null,
      timezone_name: null,
      logo: null
    },
    options: {
      timezones: []
    },
    errors: {
      name: null,
      timezone_name: null,
      logo: null
    },
    loading: false,
    deleteLoading: false,
    currentLogo: null
  }),

  mixins: [formErrorsMixin],

  computed: {
    lang () {
      return this.$store.getters['account/lang']
    },

    isValid () {
      return this.form.name && this.form.timezone_name
    }
  },

  async mounted () {
    await this.loadOptions()
    this.$nextTick(() => {
      this.initializeForm()
    })
  },

  watch: {
    country: {
      handler () {
        this.initializeForm()
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    initializeForm () {
      if (this.country) {
        this.form.name = this.country.name || null
        this.form.timezone_name = this.country.timezone_name || 'Europe/Kyiv'
        this.currentLogo = this.country.logo || null
      }
    },

    async onUpdate () {
      this.loading = true

      this.checkValidData()

      if (this.errors.name === null && this.errors.timezone_name === null && this.errors.logo === null) {
        try {
          const formData = new FormData()
          formData.append('name', this.form.name)
          formData.append('timezone_name', this.form.timezone_name)

          if (this.form.logo) {
            formData.append('logo', this.form.logo)
          } else if (!this.currentLogo) {
            formData.append('logo', '')
          }

          const countryData = {
            id: this.country.id,
            payload: formData,
            name: this.form.name,
            timezone_name: this.form.timezone_name,
            logo: this.form.logo || this.currentLogo
          }

          this.$emit('submit', countryData)
        } catch (error) {
          console.error('Помилка підготовки даних для оновлення:', error)
          if (error.response && error.response.data) {
            const serverErrors = error.response.data
            if (serverErrors.name) this.errors.name = serverErrors.name
            if (serverErrors.timezone_name) this.errors.timezone_name = serverErrors.timezone_name
            if (serverErrors.logo) this.errors.logo = serverErrors.logo
          }
        }
      }

      this.loading = false
    },

    async onDelete () {
      try {
        this.deleteLoading = true
        this.$emit('delete', this.country.id)
        this.$emit('close')
      } catch (error) {
        console.error('Помилка видалення країни:', error)
      } finally {
        this.deleteLoading = false
      }
    },

    checkValidData () {
      this.errors.name = null
      this.errors.timezone_name = null
      this.errors.logo = null

      if (!this.form.name) {
        this.errors.name = ['3']
      } else if (this.form.name.length > 100) {
        this.errors.name = ['6', 100]
      }

      if (!this.form.timezone_name) {
        this.errors.timezone_name = ['3']
      }

      if (this.form.logo) {
        const maxSize = 5 * 1024 * 1024
        if (this.form.logo.size > maxSize) {
          this.errors.logo = ['7']
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        if (!allowedTypes.includes(this.form.logo.type)) {
          this.errors.logo = ['8']
        }
      }
    },

    async loadOptions () {
      try {
        const { data } = await api.local.timezones()
        this.options.timezones = Object
          .entries(data)
          .filter((item) => {
            if (this.lang === 'us') {
              return item[0].includes('America')
            } else return true
          })
          .map(item => ({
            name: `(${item[1]}) ${item[0]}`,
            value: item[0]
          }))
      } catch (error) {
        console.error('Помилка завантаження часових зон:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__content {
    margin-top: 2rem;
    border-top: .1rem solid #d7d7d7;
    padding: 2rem;
    padding-top: 4rem;
}
.form-element {
  margin-bottom: 3rem;
}
.v-card__actions {
  .card-actions-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .main-actions {
    flex: 1;

    button {
      width: 100%;
    }
  }

  .secondary-actions {
    margin-left: 1rem;
  }

  .delete-btn {
    color: #d32f2f !important;

    .v-icon {
      color: #d32f2f !important;
    }

    &:hover {
      background-color: rgba(211, 47, 47, 0.1) !important;
    }
  }
}

.current-logo {
  margin-top: 0.8rem;

  .logo-preview {
    max-width: 10rem;
    max-height: 5rem;
    border-radius: 0.4rem;
    border: 0.1rem solid #e0e0e0;
    object-fit: cover;
  }
}

.form-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.4rem;
  display: block;
}
</style>